import { computed, inject, Injectable, signal } from '@angular/core';
import { ThemeStore } from 'shared';
import type { SizeValue } from './themed-icon.types';

function toPixels(value: SizeValue): number {
  if (typeof value === 'number') return value;
  if (value.endsWith('rem')) return Math.round(Number.parseFloat(value) * 16);
  return Math.round(Number.parseFloat(value));
}

function toCssValue(value: SizeValue): string {
  return typeof value === 'number' ? `${value}px` : value;
}

@Injectable()
export class ThemedIconStore {
  private readonly themeStore = inject(ThemeStore);

  /** Input mirrors — synced from component inputs via effects */
  readonly name = signal<string>('');
  readonly size = signal<SizeValue>(24);
  readonly width = signal<SizeValue | undefined>(undefined);
  readonly height = signal<SizeValue | undefined>(undefined);
  readonly ariaLabel = signal<string>('');
  readonly alt = signal<string>('');
  readonly mode = signal<'light' | 'dark' | 'swap' | undefined>(undefined);

  /** Derived state */
  readonly resolvedSrc = computed(() => {
    const mode = this.mode();
    const isDark = this.themeStore.isDarkMode();
    const variant =
      mode === 'swap' ? (isDark ? 'light' : 'dark') : (mode ?? (isDark ? 'dark' : 'light'));
    return `assets/icons/${variant}/${this.name()}`;
  });

  private readonly resolvedWidth = computed(() => this.width() ?? this.size());
  private readonly resolvedHeight = computed(() => this.height() ?? this.size());

  readonly resolvedWidthPx = computed(() => toPixels(this.resolvedWidth()));
  readonly resolvedHeightPx = computed(() => toPixels(this.resolvedHeight()));
  readonly resolvedWidthCss = computed(() => toCssValue(this.resolvedWidth()));
  readonly resolvedHeightCss = computed(() => toCssValue(this.resolvedHeight()));
}
